import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '../../../common/components/Button';
import Stack from '../../../common/components/primitives/Stack';
import Cluster from '../../../common/components/primitives/Cluster';
import Text from '../../../common/components/base/Text';

/**
 * TECH-2730. THE RESYNC BUTTON — what the two startup seeds used to do on
 * every boot of every replica, now done once, when an administrator asks.
 *
 * It exists because nothing seeds at startup any more (decided 2026-09-23
 * after seedMessageLanguages crash-looped dev3's ddp-server pods 39 and 41
 * times). A deployment that needs its organisations backfilled — after a
 * deploy, after an import, after this feature first lands — clicks this once.
 * It reads every patient record once per organisation, so it confirms first
 * and reports what it did, including which organisations failed.
 *
 * Rendered only for a user holding ADMIN_RESYNC_MESSAGE_TEMPLATE; the parent
 * decides that. State about the run (in flight, result, failure) is the
 * parent's too, because the method call lives there with the dispatch.
 */
const ResyncAllControl = ({
  onConfirm,
  resyncing,
  result,
  failed,
  alreadyRunning,
}) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = useCallback(() => {
    setConfirming(false);
    onConfirm();
  }, [onConfirm]);

  return (
    <Stack data-testid="resync-all">
      <Cluster>
        <Button
          data-testid="resync-all-button"
          onClick={() => setConfirming(true)}
          disabled={resyncing || confirming}
        >
          {resyncing
            ? t('resyncing', { defaultValue: 'Resyncing…' })
            : t('resyncAllMessageTemplates', {
                defaultValue: 'Resync all organisations',
              })}
        </Button>
      </Cluster>

      {confirming && (
        <Stack
          data-testid="resync-all-confirmation"
          className="border border-warning bg-warning-muted rounded-md p-4"
        >
          <Text>
            {t('resyncAllConfirm', {
              defaultValue:
                'This adds any missing message rows and recomputes the patient language counts for every organisation you administer. It reads every patient record once per organisation and can take a few minutes. Existing wording and chosen language sets are not changed.',
            })}
          </Text>
          <Cluster>
            <Button
              data-testid="resync-all-cancel"
              onClick={() => setConfirming(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              type="primary"
              data-testid="resync-all-confirm"
              onClick={handleConfirm}
            >
              {t('resyncAllConfirmOk', { defaultValue: 'Resync now' })}
            </Button>
          </Cluster>
        </Stack>
      )}

      {alreadyRunning && (
        // Distinct from `failed`: a resync IS running on the server, started
        // by someone (possibly this administrator, before a reconnect). Saying
        // "nothing was changed" here would be false.
        <Stack data-testid="resync-all-already-running">
          <Text size="small" importance="low">
            {t('resyncAllAlreadyRunning', {
              defaultValue:
                'A resync is already running on the server. Wait for it to finish, then check the counts.',
            })}
          </Text>
        </Stack>
      )}

      {failed && (
        // Wrapped in a Stack for the data-testid: `Text` drops unknown props.
        <Stack data-testid="resync-all-failed">
          <Text size="small" importance="low">
            {t('resyncAllFailed', {
              defaultValue:
                'The resync could not be started. Nothing was changed.',
            })}
          </Text>
        </Stack>
      )}

      {result && (
        // The report. Numbers with a source: what the server did, not what
        // the client assumes it did.
        <Stack data-testid="resync-all-result">
          <Text size="small" importance="low">
            {t('resyncAllDone', {
              resynced: result.resynced,
              organisations: result.organisations,
              inserted: result.inserted,
              seconds: Math.round(result.tookMs / 1000),
              defaultValue:
                'Resynced {{resynced}} of {{organisations}} organisations, {{inserted}} message rows added, in {{seconds}}s.',
            })}
          </Text>
          {result.failed && result.failed.length > 0 && (
            <Text size="small" importance="low">
              {t('resyncAllPartial', {
                names: result.failed.map((entry) => entry.name).join(', '),
                defaultValue:
                  'Failed for: {{names}}. Press again to retry them.',
              })}
            </Text>
          )}
          {/* Every number the server returns is shown. A run in which every
              organisation was skipped used to read "Resynced 0 of 12 … in 0s"
              with nothing else — a successful-looking report for a run that
              did nothing. */}
          {result.skipped > 0 && (
            <Text size="small" importance="low">
              {t('resyncAllSkipped', {
                count: result.skipped,
                defaultValue:
                  '{{count}} skipped — a Refresh was already running for them, or they changed while this run was going. Press again later.',
              })}
            </Text>
          )}
          {result.stoppedEarly && (
            <Text size="small" importance="low">
              {t('resyncAllStoppedEarly', {
                remaining: result.remaining,
                defaultValue:
                  'Stopped at the time limit with {{remaining}} organisations remaining. Press again to continue — it is safe to repeat.',
              })}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};

ResyncAllControl.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  resyncing: PropTypes.bool,
  result: PropTypes.shape({
    organisations: PropTypes.number,
    resynced: PropTypes.number,
    inserted: PropTypes.number,
    skipped: PropTypes.number,
    stoppedEarly: PropTypes.bool,
    remaining: PropTypes.number,
    tookMs: PropTypes.number,
    failed: PropTypes.arrayOf(
      PropTypes.shape({
        organisationId: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
  }),
  failed: PropTypes.bool,
  alreadyRunning: PropTypes.bool,
};

ResyncAllControl.defaultProps = {
  resyncing: false,
  result: null,
  failed: false,
  alreadyRunning: false,
};

export default ResyncAllControl;
